import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import FlowView from '@/views/FlowView.vue'
import HomeView from '@/views/HomeView.vue'
import ProjectsView from '@/views/ProjectsView.vue'
import ClustersView from '@/views/ClustersView.vue'
import AccountView from '@/views/AccountView.vue'
import BillingView from '@/views/BillingView.vue'
import RessourcesUsageView from '@/views/RessourcesUsageView.vue'
import ConnectedServicesView from '@/views/ConnectedServicesView.vue'
import ClusterMonitoringView from '@/views/ClusterMonitoringView.vue'
import AuthenticationErrorView from '@/views/AuthenticationErrorView.vue'
import store from '@/store'
import API from '@/api/wrapper'
import { APIUser, APISocialAccount, APICustomer } from '@/typesAPI'
import { redirectToLogin } from '@/utils/authentication'


const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    redirect: { name: 'projects' },
    component: HomeView,
    props: { needAuth: true },
    children: [
      {
        // ProjectsView will be rendered inside HomeView's <router-view>
        // when /projects is matched
        path: 'projects',
        name: 'projects',
        component: ProjectsView,
      },
      {
        path: 'clusters/:action?/:clusterId?',
        name: 'clusters',
        component: ClustersView,
      },
      {
        path: 'account',
        name: 'account',
        component: AccountView,
      },
      {
        path: 'billing',
        name: 'billing',
        component: BillingView,
      },
      {
        path: 'connected-services',
        name: 'connectedServices',
        component: ConnectedServicesView,
      },
      {
        path: 'usage',
        name: 'usage',
        component: RessourcesUsageView,
      },
    ]
  },
  {
    path: '/cluster/:clusterId',
    name: 'clusterMonitoring',
    component: ClusterMonitoringView,
    props: { needAuth: true }
  },
  {
    path: '/authentication-error',
    name: 'authenticationError',
    component: AuthenticationErrorView,
    props: route => ({
      status: typeof route.query.status === 'string' ? route.query.status : undefined,
      reason: typeof route.query.reason === 'string' ? route.query.reason : undefined,
    }),
  },
  {
    path: '/projects/:projectId/flow',
    name: 'flow',
    component: FlowView,
    props: { needAuth: true }
  },
  {
    path: '/projects/:projectId/flow/block/:blockId/tab/:tabId',
    name: 'flowBlock',
    component: FlowView,
    props: { needAuth: true }
  },
  {
    path: '/projects/:projectId/flow/block/:blockId/tab/:tabId/box/:boxId',
    name: 'flowBlockAndBox',
    component: FlowView,
    props: { needAuth: true }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach((to, from, next) => {
  const needAuth = (to.matched[0]?.props.default as any).needAuth
  if (needAuth && !store.getters['user/getUser']) {
    API.user.getMe()
      .then((res: { user: APIUser, socialAccounts: APISocialAccount[], customers: APICustomer[] }) => {
        res.socialAccounts.forEach((socialAccount: APISocialAccount) => {
          store.dispatch('user/setSocialAccount', { provider: socialAccount.attributes.provider, socialAccount: socialAccount })
        })
        store.dispatch('user/setUser', res.user)

        next()
      })
      .catch((err) => {
        const status = err?.response?.status

        if (status === 401) {
          redirectToLogin(window.location.href)
          return
        }

        const errors = err?.response?.data?.errors
        const reason = errors && typeof errors === 'object'
          ? Object.keys(errors)[0]
          : undefined

        next({
          name: 'authenticationError',
          query: {
            ...(status ? { status: String(status) } : {}),
            ...(reason ? { reason } : {}),
          },
          replace: true,
        })
      })
  } else {
    next()
  }
})

export default router
